import React from 'react'
import styled from 'styled-components'
import { Close } from 'sutra/components/Icons'
import {
  Chevron,
  Spinner,
  TriggerLabel,
} from 'sutra/components/Select/select-styles'
import { brand, gray } from 'sutra/tokens/colors'

const ClearIcon = styled.span`
  display: flex;
  flex-shrink: 0;
  padding: 2px;
  color: ${gray[500]};
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    color: ${brand.stillNight[800]};
  }
`

const swallow = (event: React.SyntheticEvent): void => event.stopPropagation()

// The clear control of a plain or searchable single select. The trigger is a
// <button>, which can hold no second <button>, so this is a role="button"
// span; its presses are swallowed so they neither open the panel nor commit
// anything.
export function ClearMark({
  onClear,
}: {
  onClear: () => void
}): React.ReactElement {
  return (
    <ClearIcon
      role="button"
      aria-label="Clear"
      tabIndex={-1}
      onPointerDown={swallow}
      onMouseDown={swallow}
      onClick={(event) => {
        event.stopPropagation()
        onClear()
      }}
    >
      <Close width={12} height={12} />
    </ClearIcon>
  )
}

// What ends the field: a spinner while loading, else the caller's icon, else
// the chevron.
export function Suffix({
  loading,
  suffixIcon,
}: {
  loading?: boolean
  suffixIcon?: React.ReactNode
}): React.ReactElement {
  if (loading) return <Spinner aria-hidden />
  if (suffixIcon !== undefined) return <>{suffixIcon}</>
  return <Chevron />
}

interface TriggerContentProps {
  labelId: string
  label: React.ReactNode
  showPlaceholder: boolean
  icon?: React.ReactNode
  accessory?: React.ReactNode
  onClear?: () => void
  loading?: boolean
  suffixIcon?: React.ReactNode
}

// What a single select's trigger holds, in order: icon, label (or the muted
// placeholder), accessory, clear control, suffix.
export function SelectTriggerContent({
  labelId,
  label,
  showPlaceholder,
  icon,
  accessory,
  onClear,
  loading,
  suffixIcon,
}: TriggerContentProps): React.ReactElement {
  return (
    <>
      {icon}
      <TriggerLabel
        id={labelId}
        data-placeholder={showPlaceholder || undefined}
      >
        {label}
      </TriggerLabel>
      {accessory}
      {onClear && <ClearMark onClear={onClear} />}
      <Suffix loading={loading} suffixIcon={suffixIcon} />
    </>
  )
}
